import React from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useAuth} from '../../providers/AuthProvider'
import {useAPI} from '../../config/API'
import {Table, Statistic} from 'antd'
import SystemNotification from '../../models/SystemNotification'

// ─── Utilities ───────────────────────────────────────────────────────────────

const cx = (...classes) => classes.filter(Boolean).join(' ')

// ─── Shared class strings ─────────────────────────────────────────────────────

const btnPrimary = 'px-5 py-2.5 bg-indigo-600 text-white text-[13.5px] font-semibold cursor-pointer hover:bg-indigo-700 transition-colors flex items-center gap-1.5'


// ─── Sent Log columns ─────────────────────────────────────────────────────────

const makeLogColumns = (navigate) => [
    {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        render: (text) => (
            <span className="text-[13.5px] font-medium text-gray-900">{text || 'Untitled'}</span>
        ),
    },
    {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        render: (cat) => {
            const {label, cls} = SystemNotification.category(cat)
            return (
                <span className={cx('text-[11px] font-semibold px-2 py-0.5 rounded-full', cls)}>
                    {label}
                </span>
            )
        },
    },
    {
        title: 'Audience',
        key: 'audience',
        render: (_, record) => (
            <span className="text-[13px] text-gray-500">
                {SystemNotification.audienceLabel(record)}
            </span>
        ),
    },
    {
        title: 'Sent',
        dataIndex: 'created_at',
        key: 'sent',
        render: (d) => (
            <span className="text-[13px] text-gray-500">
                {new Date(d).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
            </span>
        ),
    },
    {
        title: 'Recipients',
        dataIndex: 'user_notifications_count',
        key: 'recipients',
        render: (n, record) => (
            <span className="text-[13.5px] font-semibold">
                {n > 0
                    ? n
                    : record.failed_at
                        ? <span className="italic opacity-60">—</span>
                        : <span className="italic opacity-60">Pending</span>
                }
            </span>
        ),
    },
    {
        title: 'Status',
        key: 'status',
        render: (_, record) => {
            if (record.failed_at) {
                const failedDate = new Date(record.failed_at).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                })
                return (
                    <span
                        className="flex items-center gap-1.5 text-[12.5px] font-medium text-red-600 whitespace-nowrap"
                        title={`Failed at ${failedDate}`}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"/>
                        Failed
                    </span>
                )
            }
            if (record.user_notifications_count > 0) {
                return (
                    <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-emerald-600 whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"/>
                        Sent
                    </span>
                )
            }
            return (
                <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-yellow-600 whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0"/>
                    <i>Sending</i>
                </span>
            )
        },
    },
    {
        title: '',
        key: 'actions',
        render: (_, record) => (
            <div className="flex gap-1.5">
                <button
                    onClick={() => navigate(`${record.id}`)}
                    className="text-[12px] px-2.5 py-1 border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                    View
                </button>
            </div>
        ),
    },
]


// ─── Admin Messaging Page ─────────────────────────────────────────────────────

const AdminMessaging = () => {
    const {auth} = useAuth()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const currentPage = parseInt(searchParams.get('page') || '1', 10)

    const {data, isLoading, isError} = useAPI(
        ['fetchSystemNotifications', {page: currentPage}]
    )

    const notifications = data?.notifications ?? []
    const total = data?.total ?? 0
    const perPage = data?.per_page ?? 25

// ─── Stat cards ───────────────────────────────────────────────────────────────

    const STATS = [
        {label: 'Total Sent',     value: data?.stats?.total,    suffix: '',  desc: 'messages this month'},
        {label: 'Total Reached',  value: data?.stats?.reach,  suffix: '',  desc: 'individual deliveries'},
        {label: 'Read Rate',      value: data?.stats?.total > 0  ? Number(((data?.stats?.viewed || 0) / (data?.stats?.reach || 0) * 100)).toFixed(2) : 0,    suffix: '%', desc: 'avg open rate'},
        // {label: 'Drafts',         value: 2,     suffix: '',  desc: 'pending review'},
    ]
    const goToPage = (page) => {
        setSearchParams(page > 1 ? {page: String(page)} : {})
    }

    return (
        <div className="flex flex-col w-full min-h-screen bg-gray-100 text-gray-900 text-sm">

            {/* Header */}
            <div className="px-7 py-5 bg-black flex items-center justify-between shrink-0">
                <div>
                    <h1 className="text-lg font-bold tracking-tight text-white">Sent Messages</h1>
                    <p className="text-[13px] text-gray-400 mt-0.5">Message history and delivery log</p>
                </div>
                <button onClick={() => navigate('new')} className={btnPrimary}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Compose Message
                </button>
            </div>

            {/* Body */}
            <div className="flex-1 py-6 px-7 overflow-auto">

                {/* Stat cards */}
                <div className="grid grid-cols-3 gap-3.5 mb-5">
                    {STATS.map(({label, value, suffix, desc}) => (
                        <div key={label} className="bg-white border border-gray-200 p-4">
                            <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">
                                {label}
                            </div>
                            <Statistic
                                value={value}
                                suffix={suffix}
                                loading={!data?.stats}
                                valueStyle={{fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.2}}
                            />
                            <div className="text-[12px] text-gray-400 mt-0.5">{desc}</div>
                        </div>
                    ))}
                </div>

                {/* Notifications table */}
                <Table
                    columns={makeLogColumns(navigate)}
                    dataSource={notifications}
                    rowKey="id"
                    loading={isLoading}
                    locale={{
                        emptyText: isError
                            ? 'Failed to load messages.'
                            : 'No messages sent yet.'
                    }}
                    pagination={{
                        current: currentPage,
                        pageSize: perPage,
                        total,
                        onChange: goToPage,
                        showSizeChanger: false,
                        showTotal: (t) => `${t} messages`,
                    }}
                />
            </div>
        </div>
    )
}

export default AdminMessaging